// Copyright 2026. WebPros International GmbH. All rights reserved.

import styled from 'styled-components';
import { COLORS } from 'admin/core/theme';

export const DialogOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${COLORS.WHITE};
    z-index: 100000;
    display: flex;
    flex-direction: column;
`;

export const DialogHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid ${COLORS.GREY_30};
    min-height: 56px;
`;

export const DialogHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const DialogTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.GREY_90};
`;

export const DialogBody = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

export const FormPanel = styled.div`
    width: 400px;
    min-width: 400px;
    border-right: 1px solid ${COLORS.GREY_30};
    padding: 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
`;

export const ContentPanel = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export const ContentHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid ${COLORS.GREY_30};
`;

export const ContentBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0;
`;

export const ButtonGroup = styled.div`
    display: flex;
    gap: 8px;
    margin-top: auto;
    padding-top: 24px;
`;

export const Placeholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${COLORS.GREY_70};
    font-size: 14px;
    padding: 24px;
    text-align: center;
`;

export const BreadcrumbContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    overflow: hidden;
    white-space: nowrap;
    min-width: 0;
`;

export const BreadcrumbStep = styled.span<{ isActive: boolean; isClickable: boolean }>`
    color: ${props => props.isActive ? COLORS.GREY_90 : COLORS.GREY_70};
    font-weight: ${props => props.isActive ? 600 : 400};
    cursor: ${props => props.isClickable ? 'pointer' : 'default'};

    &:hover {
        ${props => props.isClickable ? 'text-decoration: underline;' : ''}
    }
`;

export const BreadcrumbSeparator = styled.span`
    color: ${COLORS.GREY_70};
`;

export const TableRow = styled.div<{ isDisabled?: boolean }>`
    display: flex;
    align-items: center;
    padding: 12px 24px;
    border-bottom: 1px solid ${COLORS.GREY_30};
    font-size: 13px;
    opacity: ${props => props.isDisabled ? 0.6 : 1};
    background: ${props => props.isDisabled ? COLORS.GREY_10 : 'transparent'};

    &:hover {
        background: ${props => props.isDisabled ? COLORS.GREY_10 : COLORS.GREY_20};
    }
`;

export const TableCell = styled.div<{ width?: string; flex?: number }>`
    ${props => props.width ? `width: ${props.width}; min-width: ${props.width};` : ''}
    ${props => props.flex ? `flex: ${props.flex};` : ''}
    padding-right: 12px;
`;

export const CellSubtext = styled.div`
    font-size: 11px;
    color: ${COLORS.GREY_70};
    margin-top: 2px;
`;

export const SectionHeader = styled.div`
    padding: 12px 24px;
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.GREY_80};
    text-transform: uppercase;
    background: ${COLORS.GREY_20};
    border-bottom: 1px solid ${COLORS.GREY_30};
`;

export const TableHeaderRow = styled.div`
    display: flex;
    align-items: center;
    padding: 8px 24px;
    border-bottom: 1px solid ${COLORS.GREY_30};
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.GREY_80};
    text-transform: uppercase;
`;

// Warning palette is not represented in admin/core/theme; keep literals for the banner semantic.
export const WarningBanner = styled.div`
    padding: 12px 24px;
    background: #fef9e7;
    border-bottom: 1px solid #f0e68c;
    font-size: 13px;
    color: #856404;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const SearchContainer = styled.div`
    width: 240px;
    min-width: 240px;
    flex-shrink: 0;
`;


export const LoadMoreLink = styled.span<{ isDisabled?: boolean }>`
    cursor: ${props => props.isDisabled ? 'default' : 'pointer'};
    color: inherit;
    pointer-events: ${props => props.isDisabled ? 'none' : 'auto'};

    a& , & {
        color: inherit;
    }
`;
